import {
  appRoutes,
  buildDtaSearchRequest,
  fiduciarieMessages,
  fiduciarieSliceActions,
  searchDta,
  useAppDispatch,
  useAppSelector
} from 'azshared';
import type { DtaListedAccount } from 'azshared';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useNavigate } from 'react-router-dom';
import { AppBreadcrumb } from '../../../components/breadcrumb/app-breadcrumb';
import { Card, CardSize } from '../../../components/card/card';
import { useGlobalActions } from '../../../hooks/global-actions';
import { FiduciarieDeleteModal } from './fiduciarie-delete-modal';
import './fiduciarie-page.scss';
import { FiduciarieSearchFilters } from './fiduciarie-types';
import { FiduciarieResultsTable } from './results/fiduciarie-results-table';
import { FiduciarieSearchFilter } from './search-filter/fiduciarie-search-filter';

export const FiduciariePage = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showToast, closeToast } = useGlobalActions();
  const [filters, setFilters] = useState<FiduciarieSearchFilters>();
  // The account whose delete is waiting for the confirmation of the modal.
  const [accountToDelete, setAccountToDelete] = useState<DtaListedAccount>();

  const { searchDtaResponse, searchDtaError } = useAppSelector((state) => state.features.adminConsole.fiduciarie);
  const accounts = searchDtaResponse?.data ?? [];
  const pagination = searchDtaResponse?.pagination;
  const currentPage = pagination?.paginaCorrente ?? 1;

  const search = useCallback(
    (searchFilters: FiduciarieSearchFilters | undefined, page: number) => {
      dispatch(searchDta(buildDtaSearchRequest(searchFilters, page)));
    },
    [dispatch]
  );

  useEffect(() => {
    search(undefined, 1);
  }, [search]);

  useEffect(() => {
    if (searchDtaError) {
      showToast({
        type: 'error',
        title: formatMessage(fiduciarieMessages.searchError),
        description: searchDtaError.campiNonValidi?.length
          ? formatMessage(fiduciarieMessages.invalidFieldsDescription, { fields: searchDtaError.campiNonValidi.join(', ') })
          : undefined,
        close: closeToast
      });
      dispatch(fiduciarieSliceActions.resetSearchDtaState());
    }
  }, [searchDtaError, dispatch, formatMessage, showToast, closeToast]);

  const handleSubmit = useCallback(
    (searchFilters: FiduciarieSearchFilters) => {
      setFilters(searchFilters);
      search(searchFilters, 1);
    },
    [search]
  );

  const handleReset = useCallback(() => {
    setFilters(undefined);
    search(undefined, 1);
  }, [search]);

  const handleNavigateToCreate = useCallback(() => {
    navigate(appRoutes.AdminFiduciarieCreate);
  }, [navigate]);

  const handleDelete = useCallback((account: DtaListedAccount) => {
    setAccountToDelete(account);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setAccountToDelete(undefined);
  }, []);

  const handleDeleted = useCallback(() => {
    search(filters, currentPage);
  }, [search, filters, currentPage]);

  const handleOpenDetail = useCallback(
    (account: DtaListedAccount) => {
      navigate(generatePath(appRoutes.AdminFiduciarieDetail, { username: account.username }));
    },
    [navigate]
  );

  const handleShowPage = useCallback(
    (page: number) => {
      search(filters, page);
    },
    [search, filters]
  );

  const handleShowPreviousPage = useCallback(() => {
    search(filters, Math.max(currentPage - 1, 1));
  }, [search, filters, currentPage]);

  const handleShowNextPage = useCallback(() => {
    search(filters, Math.min(currentPage + 1, pagination?.pagineTotali ?? currentPage));
  }, [search, filters, currentPage, pagination]);

  return (
    <div className='fiduciarie-page'>
      <div className='fiduciarie-card'>
        <AppBreadcrumb />
        <Card title={formatMessage(fiduciarieMessages.pageTitle)} sizeVariant={CardSize.MAXI}>
          <FiduciarieSearchFilter onSubmit={handleSubmit} onReset={handleReset} />

          <div className='results-table-container'>
            <div className='results-table-header-row'>
              <div className='title-result-table'>{formatMessage(fiduciarieMessages.resultsSectionTitle)}</div>
              <button type='button' className='btn-add-fiduciaria' onClick={handleNavigateToCreate} title='Aggiungi utenza fiduciaria'>
                +
              </button>
            </div>
            <FiduciarieResultsTable
              accounts={accounts}
              pagination={pagination}
              showPage={handleShowPage}
              showNextPage={handleShowNextPage}
              showPreviousPage={handleShowPreviousPage}
              onDelete={handleDelete}
              onOpen={handleOpenDetail}
            />
          </div>
        </Card>
      </div>

      <FiduciarieDeleteModal account={accountToDelete} onClose={handleCancelDelete} onDeleted={handleDeleted} />
    </div>
  );
};
